import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
} from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { formatQuota } from 'pages/Scheduling/EnrollmentCount'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { LiaFileExportSolid } from 'react-icons/lia'
import SchedulingCourseSessionsSearchBar from './Search'
import SchedulingCourseSessionsList from './List'
import { useDispatch, useSelector } from 'react-redux'
import { search, getSchoologyData } from 'store/scheduling/course-session'
import { useEffect, useRef, useState } from 'react'
import { omit } from 'lodash'
import { getCourseSessionsEnrollmentType } from 'store/lookup'

export default function SchedulingCourseSessionsListIndex() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { academicYear } = useSelector(state => state.session)
  const { count, query, list } = useSelector(state => state.scheduling.courseSession)
  const staffRole = useSelector(state => state.lookup?.courseSessionsEnrollmentType)
  const academicYearMarkingPeriods = useSelector(state => state.session?.academicYear?.markingPeriods)

  const [exportLoading, setExportLoading] = useState(false)
  const [schoologyExportLoading, setSchoologyExportLoading] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const anchorRef = useRef(null)

  useEffect(() => {
    const courseSessionQuery = { ...omit(query, 'courseId'), offset: 0 }
    if (!list) dispatch(search(courseSessionQuery))
  }, [academicYear])

  useEffect(() => {
    staffRole || dispatch(getCourseSessionsEnrollmentType())
  }, [])

  const basePath = '/scheduling/course-sessions/'

  const getTerm = mp => {
    if (academicYearMarkingPeriods?.length === 1 && mp?.length === 1) return 'Summer'
    if (mp?.length === academicYearMarkingPeriods?.length) return 'Full Year'
    return mp?.map(item => item.name).toString()
  }

  const exportCSV = data => {
    if (!data) return
    const headers = ['No', 'Course Name', 'Session Name', 'Teacher', 'Term', 'Enrollment', 'Period Count']
    const csvRows = data.map((courseSession, index) => {
      const teachers = courseSession.courseSessionEnrollments
        .map(cse => `${cse.firstName} ${cse.lastName}`)
        .join(' - ')
      const term = getTerm(courseSession.markingPeriods).replaceAll(',', ' - ')
      return [
        index + 1,
        courseSession?.course?.name || ' ',
        courseSession?.name.replaceAll('#', ' ') || ' ',
        teachers || ' ',
        term || ' ',
        `${courseSession?.enrollmentCount || 0}/${formatQuota(courseSession.quota)}`,
        `${courseSession?.periodCount || 0}`,
      ].join(',')
    })
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...csvRows].join('\n')
    const link = document.createElement('a')
    link.setAttribute('href', encodeURI(csvContent))
    link.setAttribute('download', 'course-session-list.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const exportNow = () => {
    setExportLoading(true)
    const filteredQuery = Object.fromEntries(Object.entries(query).filter(([key, value]) => key && value !== null))
    dispatch(search({ ...filteredQuery, limit: count, offset: 0, export: true })).then(response => {
      exportCSV(response?.payload?.rows)
      setExportLoading(false)
    })
  }

  const escapeValue = val => {
    if (val === null || val === undefined) return ''
    const str = String(val)
    return str.includes(',') || str.includes('"') || str.includes('\n')
      ? `"${str.replace(/"/g, '""')}"`
      : str
  }

  const downloadCSV = (filename, csvContent) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const exportSchoologyFiles = data => {
    Object.entries(data).forEach(([key, rows]) => {
      if (!Array.isArray(rows) || rows.length === 0) return
      const headers = Object.keys(rows[0])
      const csvRows = rows.map(row => headers.map(h => escapeValue(row[h])).join(','))
      downloadCSV(`${key}.csv`, [headers.join(','), ...csvRows].join('\n'))
    })
  }

  const exportSchoology = () => {
    setSchoologyExportLoading(true)
    dispatch(getSchoologyData()).then(response => {
      if (response?.payload) exportSchoologyFiles(response.payload)
      setSchoologyExportLoading(false)
    })
  }

  if (!(list && staffRole)) return null

  const isLoading = exportLoading || schoologyExportLoading

  return (
    <Box>
      <Stack flexDirection="row" justifyContent="flex-end" alignItems="center">
        <Box sx={{ pt: 2, display: 'flex', gap: 1 }}>
          <ButtonGroup
            size="small"
            variant="outlined"
            color="primary"
            ref={anchorRef}
            disabled={isLoading}
          >
            <Button
              startIcon={exportLoading ? <CircularProgress size={16} color="inherit" /> : <LiaFileExportSolid />}
              onClick={exportNow}
            >
              {exportLoading ? t('Loading...') : t('Export')}
            </Button>
            <Button
              size="small"
              onClick={() => setMenuOpen(prev => !prev)}
              aria-expanded={menuOpen || undefined}
              aria-haspopup="menu"
            >
              <ArrowDropDownIcon fontSize="small" />
            </Button>
          </ButtonGroup>

          <Popper
            open={menuOpen}
            anchorEl={anchorRef.current}
            placement="bottom-end"
            transition
            sx={{ zIndex: 'tooltip' }}
          >
            {({ TransitionProps }) => (
              <Grow {...TransitionProps}>
                <Paper elevation={3}>
                  <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
                    <MenuList autoFocusItem dense>
                      <MenuItem
                        onClick={() => {
                          setMenuOpen(false)
                          exportSchoology()
                        }}
                        disabled={schoologyExportLoading}
                      >
                        {schoologyExportLoading ? (
                          <><CircularProgress size={14} sx={{ mr: 1 }} />{t('Loading...')}</>
                        ) : (
                          t('Export Schoology Files')
                        )}
                      </MenuItem>
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>

          <Button size="small" variant="contained" color="primary" onClick={() => navigate(`${basePath}new`)}>
            {t('Add Session')}
          </Button>
        </Box>
      </Stack>
      <SchedulingCourseSessionsSearchBar />
      <SchedulingCourseSessionsList list={list} />
    </Box>
  )
}
